import React, { useState } from 'react';
import {
  MapPin,
  Stamp,
  Tag,
  Languages,
  Users,
  ShieldCheck,
} from 'lucide-react';
import Reveal from '../components/Reveal';
import { TEAM, type TeamMember } from '../content/team';

// Why Aranor — six tiles that frame what the company stands for.
// Icons all come from lucide so the visual language stays consistent
// with the rest of the app (Tiers, Locations, etc.).
const VALUES = [
  {
    Icon: MapPin,
    title: 'Real Armenia, stamped.',
    body: 'Curated venues across 11 regions — picked on foot, not from a Wikipedia list.',
  },
  {
    Icon: Stamp,
    title: 'Discounts that compound.',
    body: 'Each tier unlocks deeper savings. Repeat visits multiply — the more you travel, the more you keep.',
  },
  {
    Icon: Languages,
    title: 'Seven languages.',
    body: 'Localized end-to-end, not Google-translated. Read the country in the language closest to home.',
  },
  {
    Icon: Users,
    title: 'Built by locals.',
    body: 'A Yerevan team that knows every venue personally. We answer in EN, HY, and RU within a day.',
  },
  {
    Icon: ShieldCheck,
    title: 'Privacy by default.',
    body: 'No tracking cookies, no data sales, ever. Your stamp history belongs to you — export or delete any time.',
  },
  {
    Icon: Tag,
    title: 'Free forever.',
    body: 'Aranor itself never costs you a cent. We earn when vendors win — your discounts pay for our team.',
  },
];

const About: React.FC = () => {
  return (
    <div className="space-y-16">
      {/* Hero — same dark rounded shape as Privacy / Contact so the
          static-content pages read as a family. */}
      <section className="-mx-4 px-6 lg:px-10 py-12 bg-brand-secondary text-white rounded-3xl space-y-5">
        <p className="font-heading font-bold text-xs tracking-[0.25em] text-brand-accent-light uppercase">
          Inside Aranor
        </p>
        <h1 className="font-display text-4xl md:text-6xl leading-[1.05] text-warm-100/80">
          Built in <span className="italic text-brand-primary">Yerevan</span>,
          <br className="hidden md:block" />
          for everyone who walks it.
        </h1>
        <p className="text-warm-300 max-w-2xl leading-relaxed">
          Aranor is a small team that turned a paper notebook of favorite venues
          into a digital passport. Same notebook, more friends, deeper
          discounts. That's it.
        </p>
      </section>

      {/* Why Aranor — 6-tile value-prop grid. */}
      <section className="space-y-8">
        <div className="space-y-3">
          <p className="font-heading font-bold text-sm tracking-[0.25em] text-brand-accent uppercase">
            Why Aranor
          </p>
          <h2 className="font-display text-3xl md:text-5xl text-warm-800 leading-tight">
            What you get when you{' '}
            <span className="italic text-brand-accent">carry the passport</span>
            .
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 60} className="h-full">
              <div className="h-full bg-white border border-warm-200/80 rounded-2xl p-6 space-y-3">
                <div className="w-11 h-11 rounded-lg bg-brand-primary/15 text-brand-accent flex items-center justify-center">
                  <v.Icon className="w-5 h-5" />
                </div>
                <h3 className="font-display text-xl text-warm-800 leading-snug">
                  {v.title}
                </h3>
                <p className="text-warm-600 text-sm leading-relaxed">
                  {v.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Our team — placeholder grid; initials badges until photo
          files are dropped under /public/images/team/. */}
      <section className="space-y-8">
        <div className="space-y-3">
          <p className="font-heading font-bold text-sm tracking-[0.25em] text-brand-accent uppercase">
            Our team
          </p>
          <h2 className="font-display text-3xl md:text-5xl text-warm-800 leading-tight">
            The people{' '}
            <span className="italic text-brand-accent">behind the stamps</span>.
          </h2>
          <p className="text-warm-600 max-w-2xl leading-relaxed">
            Designers, engineers, content folks, and a couple of relentless
            travelers — based in Yerevan, working with every region of Armenia.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
          {TEAM.map((member, i) => (
            <Reveal key={member.image} delay={i * 40} className="h-full">
              <div className="space-y-3">
                <TeamAvatar member={member} />
                <p className="font-heading font-bold text-warm-800 text-sm text-center">
                  {member.name}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
};

// TeamAvatar — renders a high-quality photo (WebP with JPG
// fallback) if it loads, otherwise falls back to an initials badge.
//
// Image strategy: photos are resized to 800px on the longest edge,
// aspect ratio preserved — most source photos are portrait, so we
// ship portrait WebPs. `object-cover` + `object-top` anchors the
// face/upper body inside the square card, so the bottom of the
// photo is cropped instead of the head (the original 400x400
// center-crop chopped foreheads off).
//
// `onError` runs client-side only, so the prerendered HTML is
// identical to first paint (no hydration mismatch); the failed
// state only flips after a real 404 fires.
const TeamAvatar: React.FC<{ member: TeamMember }> = ({ member }) => {
  const [failed, setFailed] = useState(false);
  const base = `/images/team/${member.image}-800`;

  if (failed) {
    const initials = member.name
      .split(' ')
      .map((p) => p[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
    return (
      <div
        aria-hidden
        className="aspect-square rounded-2xl bg-warm-200 flex items-center justify-center font-display text-3xl text-warm-600"
      >
        {initials}
      </div>
    );
  }

  return (
    <picture>
      <source type="image/webp" srcSet={`${base}.webp`} />
      <img
        src={`${base}.jpg`}
        alt={member.name}
        onError={() => setFailed(true)}
        className="aspect-square rounded-2xl object-cover object-top w-full bg-warm-200"
        loading="lazy"
        decoding="async"
      />
    </picture>
  );
};

export default About;
